/**
 * Carbon footprint readout for the designed column.
 *
 * The code check returns an embodied-carbon result per element in
 * `selProject.data.data.output.carbon` (ng.environmental_parameters.ElementCarbonFootprint) — the
 * A1-A3 product-stage GWP, split per material. See Design/env_param.proto.
 *
 * It is shown as a chip floating over the viewport rather than as a section of the results panel,
 * so the number is on screen whether or not the results panel is open: carbon is a design driver,
 * and it only informs the section / grade / reinforcement choices if the user can see it while
 * making them. Clicking the chip expands the per-material breakdown in place.
 *
 * The figure does not wait for a code check: changing the section, the material, the column length
 * or (for concrete) the reinforcement resets it and recalculates against the /carbonfootprint
 * endpoint — see resetCarbonFootprint + updateCarbonFootprint, driven by the sync middleware, whose
 * CARBON_LIVE_UPDATE_MATERIALS decides which materials the live update exists for. That is all three
 * of them (RC-Design computes it for concrete, FEM-Design for steel and timber). Reset comes first,
 * so the readout is empty (`carbonStatus === "loading"`) while the new number is on its way rather
 * than showing the old one as if it still applied.
 *
 * The chip is always mounted, also when there is no result — before the first calculation, between a
 * reset and its result, when a recalculation fails, and when a calculation ran but returned no
 * carbon (a design service that does not compute it, or missing emission data). An empty readout
 * says "this column has a carbon figure, it just is not calculated right now", which a widget that
 * disappears does not.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@simplex/sx-icons";
import { ng, JsonNumber } from "../../ngds";
import { elementMass } from "../../helpers/elementMass";
import type { ElementDbs } from "../../helpers/elementMass";

/** proto3 doubles arrive as `number` or as one of the non-finite string literals. */
const num = (v: JsonNumber | undefined): number => (typeof v === "number" ? v : 0);

/**
 * Adaptive precision — a column is normally a few hundred kg CO2e, but a small timber section can
 * land under 10 and a big one over 1000, and in either case we want a readable number of digits.
 */
const formatKg = (kg: number): string => {
  const abs = Math.abs(kg);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return kg.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/** Emission factors span several orders of magnitude (0.745 for rebar, 411 for concrete). */
const formatFactor = (factor: number): string => {
  const abs = Math.abs(factor);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 3;
  return factor.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatQuantity = (quantity: number): string =>
  quantity.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Denominator of the emission factor, and unit of the quantity it was applied to. */
const declaredUnitLabel = (unit: string | undefined): string => {
  switch (unit) {
    case "DECLARED_UNIT_KG":
      return "kg";
    case "DECLARED_UNIT_M3":
      return "m³";
    case "DECLARED_UNIT_M2":
      return "m²";
    case "DECLARED_UNIT_M":
      return "m";
    default:
      return "";
  }
};

/**
 * Material category label. Reuses the keys the rest of the app already has for these materials
 * rather than introducing carbon-specific ones.
 */
const materialLabelKey = (type: string | undefined): string => {
  switch (type) {
    case "MTRL_CONCRETE":
      return "concrete_";
    case "MTRL_REINFORCEMENT":
      return "reinforcement_";
    case "MTRL_STEEL":
      return "steel_";
    case "MTRL_TIMBER":
      return "timber_";
    case "MTRL_MASONRY":
      return "masonry_";
    case "MTRL_FIRE_PROTECTION_STEEL":
    case "MTRL_FIRE_PROTECTION_TIMBER":
      return "fire_protection_material_";
    default:
      return "material_";
  }
};

/**
 * Colour per material category, so the share bar in the collapsed chip can be read without the
 * legend once the user has seen it expanded once. Defined as CSS variables (see
 * SXCarbonFootprint.scss) to keep the themes in control of the actual colours.
 */
const materialColorVar = (type: string | undefined): string => {
  switch (type) {
    case "MTRL_CONCRETE":
      return "var(--sx-carbon-concrete)";
    case "MTRL_REINFORCEMENT":
      return "var(--sx-carbon-reinforcement)";
    case "MTRL_STEEL":
      return "var(--sx-carbon-steel)";
    case "MTRL_TIMBER":
      return "var(--sx-carbon-timber)";
    default:
      return "var(--sx-carbon-other)";
  }
};

/** Contribution of one material, flattened out of the proto for rendering. */
interface CarbonRow {
  key: string;
  type: string | undefined;
  materialName: string;
  quantity: number;
  unit: string | undefined;
  factor: number;
  gwp: number;
  /** Fraction of the element total, 0-1. Used for the share bar and the % column. */
  share: number;
  color: string;
}

/**
 * Sums the per-element footprints into one readout.
 *
 * A column project has a single element today, but the output is a repeated field, so this adds
 * the elements up instead of assuming `carbon[0]`. Items of the same material category *and* the
 * same specific material are merged, so two elements of C40/50 read as one concrete row.
 */
const summarise = (carbon: ng.environmental_parameters.ElementCarbonFootprint_json[]) => {
  let gwp = 0;
  let gwpBiogenic = 0;
  let hasMissingData = false;
  const sources = new Set<string>();
  const merged = new Map<string, CarbonRow>();

  for (const element of carbon) {
    const footprint = element?.carbon;
    if (!footprint) continue;

    gwp += num(footprint.gwp);
    gwpBiogenic += num(footprint.gwpBiogenic);
    hasMissingData = hasMissingData || footprint.hasMissingData === true;
    if (footprint.source) sources.add(footprint.source);

    for (const item of footprint.items ?? []) {
      const materialName = item.materialId?.name ?? "";
      const key = `${item.type ?? ""}|${item.materialId?.guid ?? materialName}`;
      const existing = merged.get(key);
      if (existing) {
        existing.quantity += num(item.quantity);
        existing.gwp += num(item.gwp);
      } else {
        merged.set(key, {
          key,
          type: item.type,
          materialName,
          quantity: num(item.quantity),
          unit: item.unit,
          factor: num(item.factor),
          gwp: num(item.gwp),
          share: 0,
          color: materialColorVar(item.type),
        });
      }
    }
  }

  // Shares are taken against the summed item contributions rather than against `gwp`, so the bar
  // fills the full width even if the two disagree (they should not, but a missing-data run can).
  const itemTotal = [...merged.values()].reduce((sum, row) => sum + row.gwp, 0);
  const rows = [...merged.values()].sort((a, b) => b.gwp - a.gwp);
  for (const row of rows) {
    row.share = itemTotal > 0 ? row.gwp / itemTotal : 0;
  }

  return { gwp, gwpBiogenic, hasMissingData, source: [...sources].join(" · "), rows };
};

/** Small leaf mark. The shared icon set has no environmental icon yet. */
const LeafMark = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M13.5 2.5c0 5.5-2.8 8.6-7 8.6-1 0-1.8-.2-2.4-.5C4.6 6.9 7.6 4.2 13.5 2.5Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path d="M3 14c0-3.4 1.4-6.1 4-8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

interface SXCarbonFootprintProps {
  /**
   * Distance from the right edge of the window, in px. Passed in from App so the chip stays
   * aligned with the viewport area as the results panel opens and closes — the same thing the
   * bottom toolbar does with its inline `right`.
   */
  right: number;
  project: any;
  carbonStatus: string;
}

export const SXCarbonFootprint = ({ right, project, carbonStatus }: SXCarbonFootprintProps) => {
  const { t } = useTranslation();
  const selProject = project;
  const [expanded, setExpanded] = React.useState(false);

  const carbon = selProject?.data?.data?.output?.carbon ?? [];
  const summary = React.useMemo(() => summarise(carbon), [carbon]);
  const hasResult = summary.rows.length > 0 || summary.gwp !== 0;

  // The column's weight, by the same rule the printout uses (elementMass): the self-weight the
  // analysis is given, integrated. Shown beside the carbon rather than in its table, since those rows
  // are quantities in the unit each emission factor is declared per (m³ for concrete and timber).
  const input = selProject?.data?.data?.input;
  const weightKg = React.useMemo(() => {
    const structure = input?.structures?.[0];
    const beamJson = structure?.elements?.[0]?.beam;
    if (!input || !beamJson) return 0;
    return elementMass(
      input as unknown as ElementDbs,
      ng.beam.Data.fromObject(beamJson),
      ng.loading.Data.fromObject(structure?.loading ?? {}),
    );
  }, [input]);
  // A recalculation for the input change the user just made is in flight, and the previous figure
  // has already been reset — so this is "the number is coming", not "the number is stale".
  const updating = carbonStatus === "loading";

  const title = t("carbon_footprint_.label", "Carbon footprint");
  const unit = t("kg_co2e_.label", "kg CO₂e");
  const updatingLabel = t("carbon_updating_.label", "Updating…");
  const noResultLabel = t("carbon_no_result_.label", "Run a code check to get the carbon footprint");

  return (
    <div className="sx-carbon" style={{ right }}>
      <button
        type="button"
        className={`sx-carbon__chip${expanded ? " sx-carbon__chip--expanded" : ""}`}
        onClick={() => setExpanded((v) => !v)}
        // Nothing to expand before the first calculation, but the chip stays clickable so the
        // explanation in the panel is reachable.
        aria-expanded={expanded}
        aria-busy={updating}
        title={updating ? updatingLabel : hasResult ? title : noResultLabel}
      >
        <span className="sx-carbon__mark">
          <LeafMark />
        </span>

        <span className={`sx-carbon__figure${updating ? " sx-carbon__figure--updating" : ""}`}>
          <span className="sx-carbon__value">{hasResult ? formatKg(summary.gwp) : updating ? "…" : "–"}</span>
          <span className="sx-carbon__unit">{unit}</span>
        </span>

        {summary.hasMissingData && (
          <span
            className="sx-carbon__warning"
            title={t(
              "carbon_missing_data_.label",
              "Emission data is missing for one or more materials — the total is incomplete",
            )}
          >
            !
          </span>
        )}

        <span className="sx-carbon__chevron">
          {expanded ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
        </span>

        {hasResult && (
          <span className="sx-carbon__bar" aria-hidden="true">
            {summary.rows.map((row) => (
              <span key={row.key} style={{ width: `${row.share * 100}%`, background: row.color }} />
            ))}
          </span>
        )}
      </button>

      {expanded && (
        <div className="sx-carbon__card">
          <div className="sx-carbon__card-header">
            <span className="sx-carbon__card-title">{title}</span>
            <span className="sx-carbon__card-total">
              {hasResult ? formatKg(summary.gwp) : "–"} {unit}
            </span>
          </div>

          {!hasResult && <div className="sx-carbon__empty">{updating ? updatingLabel : noResultLabel}</div>}

          {weightKg > 0 && (
            <div className="sx-carbon__weight">
              {t("element_weight_.label", "Weight")}: {formatKg(weightKg)} kg
            </div>
          )}

          {hasResult && (
            <>
              <table className="sx-carbon__table">
                <thead>
                  <tr>
                    <th>{t("material_.label", "Material")}</th>
                    <th className="text-end">{t("quantity_.label", "Quantity")}</th>
                    <th className="text-end">{t("carbon_factor_.label", "Factor")}</th>
                    <th className="text-end">{unit}</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.rows.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <span className="sx-carbon__swatch" style={{ background: row.color }} />
                        {t(`${materialLabelKey(row.type)}.label`)}
                        {row.materialName && <span className="sx-carbon__grade">{row.materialName}</span>}
                      </td>
                      <td className="text-end">
                        {formatQuantity(row.quantity)} {declaredUnitLabel(row.unit)}
                      </td>
                      <td className="text-end">
                        {formatFactor(row.factor)}
                        <span className="sx-carbon__per-unit">/{declaredUnitLabel(row.unit)}</span>
                      </td>
                      <td className="text-end">{formatKg(row.gwp)}</td>
                      <td className="text-end">{Math.round(row.share * 100)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t("total_.label", "Total")}</td>
                    <td />
                    <td />
                    <td className="text-end">{formatKg(summary.gwp)}</td>
                    <td className="text-end">100</td>
                  </tr>
                </tfoot>
              </table>

              {/* Stored biogenic carbon, and only for materials that have any (wood) — reported as a
                  negative number, since it is carbon taken out of the atmosphere.

                  Deliberately NOT a row of the table: both design services report it *beside* the
                  A1-A3 figure and never inside it (see the emission-data notes in
                  Helper/CarbonFootprint.cs and codes/sscd000c/carbon_footprint.cpp), so the two are
                  separate quantities rather than a total and its part. As a tfoot row under
                  "Total … 100 %" it read as a subtotal of a sum it is not in — which for a timber
                  column means a −380 sitting under a 19,3 that does not contain it. */}
              {summary.gwpBiogenic !== 0 && (
                <div className="sx-carbon__biogenic">
                  {t("carbon_biogenic_.label", "Biogenic carbon stored (not included)")}:{" "}
                  {formatKg(summary.gwpBiogenic)} {unit}
                </div>
              )}

              {summary.hasMissingData && (
                <div className="sx-carbon__missing">
                  {t(
                    "carbon_missing_data_.label",
                    "Emission data is missing for one or more materials — the total is incomplete",
                  )}
                </div>
              )}

              {/* The source string from the emission database normally already names the module
                  it covers ("… module A1-A3"), so the generic scope line is only shown when
                  there is no source to state it. */}
              {summary.source ? (
                <div className="sx-carbon__source">{summary.source}</div>
              ) : (
                <div className="sx-carbon__scope">
                  {t("carbon_scope_.label", "Embodied carbon, product stage A1–A3")}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SXCarbonFootprint;
